import React, { useState, useEffect } from 'react'
import ReactDOM from 'react-dom'
import { MapContainer, TileLayer, LayersControl, GeoJSON, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { ref, getBytes } from 'firebase/storage';
import { storage, auth } from '../../../../firebase';
import { onAuthStateChanged } from 'firebase/auth';

const { BaseLayer, Overlay } = LayersControl;

async function fetchGeoJSONFromStorage(nftId, key) {
  try {
    const bytes = await getBytes(ref(storage, `geojson/${nftId}/${key}.geojson`));
    return JSON.parse(new TextDecoder().decode(bytes));
  } catch (e) {
    if (e?.code !== 'storage/object-not-found') {
      console.warn(`[FarmMap] geojson/${nftId}/${key}.geojson:`, e?.code || e?.message);
    }
    return null;
  }
}

function getColor(percent) {
  return percent === 100 ? '#3b1f0a' :
    percent > 90 ? '#4e2a0e' :
    percent > 80 ? '#6b3a14' :
    percent > 70 ? '#8b4f1e' :
    percent > 60 ? '#a8622a' :
    percent > 50 ? '#bf7a40' :
    percent > 40 ? '#cc8f57' :
    percent > 30 ? '#d9a472' :
    percent > 20 ? '#e2b98e' :
    percent > 10 ? '#e8c9a8' :
    percent > 5  ? '#edd6bc' :
    percent > 2  ? '#f0dfc9' :
    percent > 1  ? '#f3e6d4' :
                   '#f5ece0';
}

function getBoundaryAreaTotal(boundaries) {
  const main = boundaries?.features?.find(f => f.properties?.recibo);
  return main?.properties?.area ?? null;
}

function boundaryStyle(feature, areaTotal) {
  if (feature.properties?.recibo) {
    return { fillColor: '#27f97c', color: '#27f97c', weight: 2, fillOpacity: 0.15 };
  }
  const rawArea = feature.properties?.area;
  const areaHa = rawArea != null
    ? (typeof rawArea === 'object' ? rawArea.value / 10000 : rawArea)
    : 0;
  let percent = areaTotal ? (areaHa / areaTotal) * 100 : 0;
  if (percent > 100) percent = 100;
  const color = getColor(percent);
  return { fillColor: color, color: '#a07850', weight: 2, dashArray: '2', fillOpacity: 0.4 };
}

function onEachBoundaryFeature(areaTotal) {
  return function (feature, layer) {
    if (feature.properties?.recibo) {
      layer.bindPopup(`<p>Área Total: ${feature.properties.area} Ha</p>`);
      return;
    }
    const rawArea = feature.properties?.area;
    const areaHa = rawArea != null
      ? (typeof rawArea === 'object' ? (rawArea.value / 10000).toLocaleString('pt-BR') : Number(rawArea).toLocaleString('pt-BR'))
      : '?';
    const areaNum = rawArea != null
      ? (typeof rawArea === 'object' ? rawArea.value / 10000 : Number(rawArea))
      : 0;
    let percent = areaTotal ? ((areaNum / areaTotal) * 100).toFixed(2) : '?';
    if (percent > 100) percent = '100.00';
    layer.bindPopup(`<p>Área: ${areaHa} Ha</p><p>Percentual: ${percent}%</p>`);
    layer.on('mouseover', function () { this.openPopup(); });
    layer.on('mouseout', function () { this.closePopup(); });
  };
}

function geojsonToLatLngs(geojson) {
  const pts = [];
  geojson.features.forEach(f => {
    const { type, coordinates } = f.geometry;
    if (type === 'Polygon') {
      coordinates[0].forEach(([lng, lat]) => pts.push([lat, lng]));
    } else if (type === 'MultiPolygon') {
      coordinates.forEach(poly => poly[0].forEach(([lng, lat]) => pts.push([lat, lng])));
    }
  });
  return pts;
}

function getBounds(pts, pad = 0.02) {
  const lats = pts.map(([lat]) => lat);
  const lngs = pts.map(([, lng]) => lng);
  return [
    [Math.min(...lats) - pad, Math.min(...lngs) - pad],
    [Math.max(...lats) + pad, Math.max(...lngs) + pad],
  ];
}

function getCenter(bounds) {
  return [
    (bounds[0][0] + bounds[1][0]) / 2,
    (bounds[0][1] + bounds[1][1]) / 2,
  ];
}

const wrapperNormal = {
  position: 'relative',
  width: '100%',
  maxWidth: '600px',
  height: '500px',
  borderRadius: '12px',
  cursor: 'zoom-in',
};

const wrapperExpanded = {
  position: 'fixed',
  top: '5%',
  left: '5%',
  width: '90%',
  height: '90%',
  zIndex: 10001,
  borderRadius: '12px',
  overflow: 'hidden',
};

const mapFill = { width: '100%', height: '100%' };

function MapResizer({ trigger }) {
  const map = useMap();
  useEffect(() => { setTimeout(() => map.invalidateSize(), 100); }, [trigger, map]);
  return null;
}

function FarmMap({ nftId }) {
  const [expanded, setExpanded] = useState(false);
  const [areas, setAreas] = useState(undefined); // undefined = loading, null = not found
  const [desmatamento, setDesmatamento] = useState(null);
  const [reservaLegal, setReservaLegal] = useState(null);
  const [embargo, setEmbargo] = useState(null);
  const [boundaries, setBoundaries] = useState(null);

  useEffect(() => {
    if (!nftId) return;
    setAreas(undefined);
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) return;
      fetchGeoJSONFromStorage(nftId, 'areas').then(setAreas);
      fetchGeoJSONFromStorage(nftId, 'desmatamento').then(setDesmatamento);
      fetchGeoJSONFromStorage(nftId, 'reserva-legal').then(setReservaLegal);
      fetchGeoJSONFromStorage(nftId, 'embargo').then(setEmbargo);
      fetchGeoJSONFromStorage(nftId, 'boundaries').then(setBoundaries);
      unsubscribe();
    });
    return unsubscribe;
  }, [nftId]);

  if (!nftId || areas === undefined || areas === null) return null;

  const pts = geojsonToLatLngs(areas);
  if (pts.length === 0) return null;

  const maxBounds = getBounds(pts);
  const center = getCenter(maxBounds);

  const mapContent = (
    <>
      <MapResizer trigger={expanded} />
      <LayersControl position="topright">
        <BaseLayer checked name="Satellite">
          <TileLayer
            url="https://api.maptiler.com/maps/satellite/{z}/{x}/{y}.jpg?key=HD8bjlfg5Tl4K2HkKkkF"
            tileSize={512}
            zoomOffset={-1}
            crossOrigin={true}
          />
        </BaseLayer>
        <BaseLayer name="Dark">
          <TileLayer
            url="https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png"
            subdomains="abcd"
            maxZoom={20}
          />
        </BaseLayer>
        <BaseLayer name="Topo">
          <TileLayer
            url="https://api.maptiler.com/maps/topo/{z}/{x}/{y}.png?key=HD8bjlfg5Tl4K2HkKkkF"
          />
        </BaseLayer>
        <Overlay checked name="Áreas da Propriedade">
          <GeoJSON
            key={`areas-${nftId}`}
            data={areas}
            style={{ fillColor: '#27f97c', color: '#27f97c', weight: 2, fillOpacity: 0.25 }}
          />
        </Overlay>
        {desmatamento && (
          <Overlay name="Desmatamento">
            <GeoJSON
              key={`desmatamento-${nftId}`}
              data={desmatamento}
              style={{ fillColor: 'red', color: 'red', weight: 2, fillOpacity: 0.5 }}
            />
          </Overlay>
        )}
        {reservaLegal && (
          <Overlay name="Reserva Legal">
            <GeoJSON
              key={`reserva-legal-${nftId}`}
              data={reservaLegal}
              style={{ fillColor: '#ffdae0', color: '#ffdae0', weight: 2, fillOpacity: 0.5 }}
            />
          </Overlay>
        )}
        {embargo && (
          <Overlay name="Embargo">
            <GeoJSON
              key={`embargo-${nftId}`}
              data={embargo}
              style={{ fillColor: 'orange', color: 'orange', weight: 2, fillOpacity: 0.3 }}
            />
          </Overlay>
        )}
        {boundaries && (
          <Overlay name="Talhões">
            <GeoJSON
              key={`boundaries-${nftId}`}
              data={boundaries}
              style={(feature) => boundaryStyle(feature, getBoundaryAreaTotal(boundaries))}
              onEachFeature={onEachBoundaryFeature(getBoundaryAreaTotal(boundaries))}
            />
          </Overlay>
        )}
      </LayersControl>
    </>
  );

  const expandedPortal = expanded && ReactDOM.createPortal(
    <>
      <div
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 10000 }}
        onClick={() => setExpanded(false)}
      />
      <div style={wrapperExpanded}>
        <button
          onClick={() => setExpanded(false)}
          style={{
            position: 'absolute', top: 10, right: 10, zIndex: 10002,
            background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none',
            borderRadius: '50%', width: 32, height: 32, fontSize: 18,
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          ✕
        </button>
        <MapContainer
          center={center}
          zoom={14}
          style={mapFill}
          scrollWheelZoom={true}
          zoomControl={true}
          attributionControl={false}
          maxBounds={maxBounds}
          maxBoundsViscosity={1}
        >
          {mapContent}
        </MapContainer>
      </div>
    </>,
    document.body
  );

  return (
    <>
      {expandedPortal}
      <div style={wrapperNormal}>
        <MapContainer
          center={center}
          zoom={14}
          style={mapFill}
          scrollWheelZoom={false}
          zoomControl={false}
          attributionControl={false}
          maxBounds={maxBounds}
          maxBoundsViscosity={1}
        >
          {mapContent}
        </MapContainer>
        {!expanded && (
          <div
            onClick={() => setExpanded(true)}
            style={{ position: 'absolute', inset: 0, borderRadius: '12px', cursor: 'zoom-in' }}
          />
        )}
      </div>
    </>
  );
}

export default React.memo(FarmMap)
